import { useEffect, useRef, useState } from "react";
import { principles, totalBenchmarks } from "./benchmarkData";

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function useLiveSimulation() {
  const [live, setLive] = useState<{ scores: number[]; deltas: number[] }>({
    scores: principles.map((p) => p.baseScore),
    deltas: principles.map(() => 0),
  });
  const [runs, setRuns] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [status, setStatus] = useState<"evaluating" | "complete">("evaluating");
  const [updatedAt, setUpdatedAt] = useState<Date>(new Date());

  useEffect(() => {
    const id = setInterval(() => {
      setLive((prev) => {
        const scores = prev.scores.map((s, i) => {
          const jitter = (Math.random() - 0.5) * 16;
          const pull = (principles[i].baseScore - s) * 0.2;
          return clamp(Math.round(s + jitter + pull), 0, 100);
        });
        const deltas = scores.map((n, i) => n - prev.scores[i]);
        return { scores, deltas };
      });
      setRuns((prev) => {
        const next = prev + (Math.random() > 0.4 ? 1 : 2);
        return next >= totalBenchmarks ? totalBenchmarks : next;
      });
      setElapsed((prev) => prev + 1);
      setUpdatedAt(new Date());
    }, 900);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (runs >= totalBenchmarks) {
      const t = setTimeout(() => {
        setStatus("complete");
        setRuns(0);
        setElapsed(0);
      }, 2400);
      return () => clearTimeout(t);
    }
    setStatus("evaluating");
  }, [runs]);

  const overall = Math.round(live.scores.reduce((a, b) => a + b, 0) / live.scores.length);
  const passRate = Math.round(live.scores.filter((s) => s >= 75).length / live.scores.length * 100);

  return { ...live, runs, elapsed, status, updatedAt, overall, passRate };
}

function Gauge({ value }: { value: number }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;

  return (
    <div className="relative w-28 h-28 md:w-32 md:h-32 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="oklch(var(--foreground-700) / 0.35)" strokeWidth="10" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="oklch(var(--primary-500))"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.9s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl md:text-3xl font-semibold text-background-50 tabular-nums">{value}</span>
        <span className="text-[10px] text-foreground-500">/ 100</span>
      </div>
    </div>
  );
}

export default function LiveDashboard() {
  const { scores, deltas, runs, elapsed, status, updatedAt, overall, passRate } = useLiveSimulation();
  const clauseCoverage = clamp(Math.round((runs / totalBenchmarks) * 100), 0, 100);

  const statTiles = [
    { label: "Benchmarks Run", value: `${runs}/${totalBenchmarks}`, icon: "ri-bar-chart-box-line" },
    { label: "Pass Rate", value: `${passRate}%`, icon: "ri-check-double-line" },
    { label: "Clause Coverage", value: `${clauseCoverage}%`, icon: "ri-file-list-3-line" },
    { label: "Elapsed", value: `${elapsed}s`, icon: "ri-time-line" },
  ];

  return (
    <div className="bg-foreground-900 border border-foreground-800 rounded-lg p-5 md:p-7 mb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 md:mb-6">
        <div className="flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status === "complete" ? "bg-secondary-400" : "bg-primary-400"} opacity-75`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${status === "complete" ? "bg-secondary-500" : "bg-primary-500"}`} />
          </span>
          <span className="text-sm font-medium text-background-50">Live Status</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status === "complete" ? "bg-secondary-500/20 text-secondary-300" : "bg-primary-500/20 text-primary-400"}`}>
            {status === "complete" ? "PASSED" : "EVALUATING"}
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-cpu-line" />
            <span className="text-background-300/80">gpt-4o-2024-08-06</span>
          </span>
          <span className="hidden sm:flex items-center gap-1.5">
            <i className="ri-refresh-line" />
            {updatedAt.toLocaleTimeString()}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Gauge */}
        <div className="lg:col-span-3 flex flex-row lg:flex-col items-center gap-5 lg:gap-4">
          <Gauge value={overall} />
          <div className="text-center lg:text-center">
            <p className="text-xs text-foreground-500 mb-1">Overall Compliance</p>
            <p className="text-sm text-background-300/80">Aggregated across {principles.length} principles</p>
          </div>
        </div>

        {/* Principle bars + stat tiles */}
        <div className="lg:col-span-9 flex flex-col gap-5">
          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {statTiles.map((s) => (
              <div key={s.label} className="bg-foreground-800/50 border border-foreground-700/50 rounded-md p-3">
                <div className="flex items-center gap-2 text-foreground-500 mb-1.5">
                  <i className={`${s.icon} text-sm`} />
                  <span className="text-[10px] uppercase tracking-wide">{s.label}</span>
                </div>
                <p className="text-lg font-semibold text-background-50 tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Live principle bars */}
          <div className="flex flex-col gap-3.5">
            {principles.map((p, i) => {
              const color = p.color;
              const barColor =
                color === "primary"
                  ? "bg-primary-500"
                  : color === "accent"
                    ? "bg-accent-500"
                    : "bg-secondary-500";
              const delta = deltas[i];
              return (
                <div key={p.name} className="flex items-center gap-3">
                  <span className="w-28 shrink-0 text-xs text-background-300/80 flex items-center gap-1.5">
                    <i className={`${p.icon} ${color === "primary" ? "text-primary-400" : color === "accent" ? "text-accent-400" : "text-secondary-400"}`} />
                    {p.name}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-foreground-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor} transition-all duration-700 ease-out`}
                      style={{ width: `${scores[i]}%` }}
                    />
                  </div>
                  <span className="w-16 shrink-0 flex items-center justify-end gap-1">
                    <span className="text-xs font-medium text-background-50 tabular-nums">{scores[i]}</span>
                    <span
                      className={`text-[10px] font-semibold tabular-nums w-6 ${
                        delta > 0 ? "text-primary-400" : delta < 0 ? "text-accent-400" : "text-foreground-600"
                      }`}
                    >
                      {delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "—"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}